import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { CurrencyCode } from 'src/app/modules/bookkeeping/enums/currency-code';
import { UpdatedSettings } from 'src/app/modules/bookkeeping/models/settings/updated-settings';
import { TaxRate } from 'src/app/modules/bookkeeping/models/taxRate';
import { TrackingCategory } from 'src/app/modules/bookkeeping/models/tracking-categories.ts/trackingCategory';
import { QBOClass } from 'src/app/modules/bookkeeping/models/qbo-class';
import { QBOCustomer } from 'src/app/modules/bookkeeping/models/qbo-customer';
import { BatchPaymentHeader } from 'src/app/modules/payable/models/batch-payment-header-model';
import { OrganizationShortData } from 'src/app/modules/signup/models/organization-short-data';
import { environment } from 'src/environments/environment';
import { RefreshLog } from '../../modules/setting/integration/integration-sidebar/model/refreshlog';
import { HttpInternalService } from './http.service';
import { customFields } from 'src/app/modules/bookkeeping/models/custom-fields/custom-fields';
import { InvoiceDisplaySettings } from 'src/app/modules/bookkeeping/models/invoice-display-settings';
import { AppConfigService } from 'src/app/appConfig.service';

declare const chrome: any;


@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  organizationList: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  selectedOrganizationId: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  contactlist: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  organizationTrackingCategories: BehaviorSubject<TrackingCategory[]> = new BehaviorSubject<TrackingCategory[]>([]);
  trackingCategoriesState$ = this.organizationTrackingCategories.asObservable();
  organizationQBOClasses: BehaviorSubject<QBOClass[]> = new BehaviorSubject<QBOClass[]>([]);
  qboClassesState$ = this.organizationQBOClasses.asObservable();
  organizationForEdit: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  organizationForEditState$ = this.organizationForEdit.asObservable();
  companyEditModalSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  companyEditModalState$ = this.companyEditModalSubject.asObservable();
  // Org-level invoice panel display preferences. null until loaded for the org.
  invoiceDisplaySettings: BehaviorSubject<InvoiceDisplaySettings> = new BehaviorSubject<InvoiceDisplaySettings>(null);
  invoiceDisplaySettings$ = this.invoiceDisplaySettings.asObservable();

  constructor(private _httpService: HttpInternalService, private configService: AppConfigService) { }

  get selectedOrganizationDateFormat() {
    return sessionStorage.getItem('orgDateFormat');
  }

  set reloadData(value: string) {
    sessionStorage.setItem('orgDateFormat', value);
  }

  set selectedOrganizationDateFormat(value: string) {
    sessionStorage.setItem('orgDateFormat', value);
  }

  get selectedOrganizationDomen() {
    return sessionStorage.getItem('orgDomen');
  }

  set selectedOrganizationDomen(value: string) {
    sessionStorage.setItem('orgDomen', value);
  }

  set selectedOrganization(data: string) {
    if (!OrganizationService.isValidId(data)) {
      localStorage.removeItem('SelectedOrganization');
      if (typeof chrome !== 'undefined' && chrome?.storage?.local) {
        chrome.storage.local.remove('SelectedOrganizationId');
      }
      // SD-1218: announce the change as well as storing it. Anything org-scoped -- module
      // entitlements above all -- has to re-resolve, and until now this setter wrote to
      // localStorage silently, so subscribers to selectedOrganizationId never heard about a
      // switch made through it.
      this.selectedOrganizationId.next(null);
      return;
    }
    localStorage.setItem('SelectedOrganization', data);
    if (typeof chrome !== 'undefined' && chrome?.storage?.local) {
      chrome.storage.local.set({ SelectedOrganizationId: data });
    }
    this.selectedOrganizationId.next(data);
  }

  get selectedOrganization(): string {
    const value = localStorage.getItem('SelectedOrganization');
    return OrganizationService.isValidId(value) ? value : null;
  }

  private static isValidId(value: unknown): value is string {
    return typeof value === 'string'
      && value.length > 0
      && value !== 'undefined'
      && value !== 'null';
  }

  set refreshConnectionURL(data: string) {
    localStorage.setItem('refreshConnectionURL', data);
  }

  get refreshConnectionURL(): string {
    return localStorage.getItem('refreshConnectionURL');
  }

  set connectURL(data: string) {
    localStorage.setItem('connectURL', data);
  }

  get connectURL(): string {
    return localStorage.getItem('connectURL');
  }

  /**
   * The QuickBooks authorize URL for the organisation that is connecting.
   *
   * The stored URL ends in "&state=" and the callback reads the organisation from state, so the
   * caller has to supply it. Opened bare, Intuit refuses the request outright ("The state query
   * parameter is missing"). A URL that already carries a state is returned unchanged.
   */
  qboConnectUrl(connectURL: string, organisationId?: string): string {
    if (!connectURL || !/[?&]state=$/.test(connectURL)) { return connectURL; }
    const candidates = [organisationId, this.selectedOrganization, sessionStorage.organizationId];
    const id = candidates.find(x => OrganizationService.isValidId(x));
    return id ? connectURL + encodeURIComponent(id) : connectURL;
  }

  set baseURL(data: string) {
    localStorage.setItem('baseURL', data);
  }

  get baseURL(): string {
    return localStorage.getItem('baseURL');
  }

  set selectedOrganizationName(data: string) {
    localStorage.setItem('SelectedOrganizationName', data);
  }

  get selectedOrganizationName(): string {
    return localStorage.getItem('SelectedOrganizationName');
  }

  set OrganizationUseAzureAPI(data: string) {
    localStorage.setItem('OrganizationUseAzureAPI', data);
  }
  get OrganizationUseAzureAPI(): string {
    return localStorage.getItem('OrganizationUseAzureAPI');
  }

  private get baseUrl(): string {
    return this.configService.apiURL;
  }

  getOrganization(url: string) {
    return this._httpService.getRequest(url);
  }

  getSelectedOrganization(OrganizationId?) {
    // this.getContactList(OrganizationId);
    this.selectedOrganizationId.next(OrganizationId ? OrganizationId : null);
  }

  setTrackingCategories(categories: TrackingCategory[]) {
    this.organizationTrackingCategories.next(categories);
  }

  setQBOClasses(classes: QBOClass[]) {
    this.organizationQBOClasses.next(classes);
  }

  setCompanyEdit(company: any) {
    this.organizationForEdit.next(company);
  }

  getOrganizationEmail(organizationId: string, userId: string) {
    return this._httpService.getRequest<{ email: string }>(`Organisation/getOrganizationEmail?id=${organizationId}&userId=${userId}`);
  }

  /**
   * Returns FinancialYearEndDay / FinancialYearEndMonth / EndOfYearLockDate / PeriodLockDate
   * for the given organisation, sourced from dbo.Organisations.
   */
  getOrganisationFinancialSettings(organisationId: string) {
    return this._httpService.getRequest<any>(
      `Organisation/GetOrganisationFinancialSettings?organisationId=${organisationId}`
    );
  }

  getOrganizationTaxRateId(organizationId: string) {
    return this._httpService.getRequest<{ taxRateId: string, taxExclusiveRateId: string, taxBasis: number, countryCode?: string }>(`Organisation/getOrganizationTaxRateId?id=${organizationId}`);
  }

  getOrganizationTrackingCategories(organizationId: string) {
    return this._httpService.getRequest<string[]>(`TrackingCategories/getOrganizationTrackingCategories?id=${organizationId}`);
  }

  getFullOrganizationTrackingCategories(organizationId: string) {
    return this._httpService.getRequest<TrackingCategory[]>(`TrackingCategories/getFullOrganizationTrackingCategories?id=${organizationId}`);
  }

  getOrganizationTrackingCategoriesWithOptions(id: string) {
    return this._httpService.getRequest<TrackingCategory[]>(`TrackingCategories/getOrganizationTrackingCategoriesWithOptions?id=${id}`);
  }

  getOrganizationQBOClasses(organizationId: string) {
    return this._httpService.getRequest<QBOClass[]>(`QBOClasses/getOrganizationClasses?id=${organizationId}`);
  }

  getOrganizationQBOCustomers(organizationId: string) {
    return this._httpService.getRequest<QBOCustomer[]>(`QBOCustomers/getOrganizationCustomers?id=${organizationId}`);
  }

  getOrganizationDefaultCurrency(id: string) {
    return this._httpService.getRequest<{ currency: CurrencyCode }>(`Organisation/getOrganizationDefaultCurrency?id=${id}`);
  }

  getOrganizationCountry(id: string) {
    return this._httpService.getRequest<{ countryCode: string }>(`Organisation/getOrganizationCountry?id=${id}`);
  }

  updateOrganizationSettings(updatedSettings: UpdatedSettings) {
    return this._httpService.putRequest('Organisation/updateOrganizationSettings', updatedSettings);
  }

  updateOrganizationTaxSettings(updatedSettings: UpdatedSettings) {
    return this._httpService.putRequest('Organisation/updateOrganizationTaxSettings', updatedSettings);
  }

  getContactList(url: string, organizationId: any, isCustomer: any, isSupplier: any, contactId: any) {
    return this._httpService.getRequest(`${url + '?Id=' + organizationId + '&IsCustomer=' + isCustomer + '&IsSupplier=' + isSupplier + '&ContactId=' + contactId}`);
  }

  getOutstandingPayableReportList(OrganizationId, userid, startDate, endDate, ReportPeriod) {
    return this._httpService.getRequest<any>(`${'Reporting/GetOutstandingPayableReportList' + '?OrganizationId=' + OrganizationId + '&userid=' + userid + "&startDate=" + startDate + "&endDate=" + endDate + "&ReportPeriod=" + ReportPeriod}`);
  }

  getUnreconciledItemsReportList(OrganizationId, userid, startDate, endDate) {
    return this._httpService.getRequest<any>(`${'Reporting/GetUnreconciledItemsReportList' + '?OrganizationId=' + OrganizationId + "&userid=" + userid + "&startDate=" + startDate + "&endDate=" + endDate}`);
  }

  GetReportListByUser(userid, OrganizationId) {
    return this._httpService.getRequest<any>(`${'Reporting/GetReportListByUser' + "?userid=" + userid + "&OrganisationId=" + OrganizationId}`);
  }

  GetUnreconciledReportListByReport(ReportId, OrganisationID, userId) {
    return this._httpService.getRequest<any>(`${'Reporting/GetUnreconciledReportListByReport' + '?ReportId=' + ReportId + "&OrganisationID=" + OrganisationID + "&userId=" + userId}`);
  }

  GetOutstandingPayablesListByReport(ReportId, OrganizationId, userId) {
    return this._httpService.getRequest<any>(`${'Reporting/GetOutstandingPayablesListByReport' + '?ReportId=' + ReportId + "&OrganizationId=" + OrganizationId + "&userId=" + userId}`);
  }

  InsertUpdateReportsComments(commentdetailsModel) {
    var modaldata = JSON.stringify(commentdetailsModel);
    return this._httpService.postRequest<any>('Reporting/InsertUpdateReportsComments', modaldata);
  }

  GetTaskReportDetails(userid, OrganizationId, Status) {
    return this._httpService.getRequest<any>(`${'Reporting/GetTaskReportDetails?userId=' + userid + '&organisationId=' + OrganizationId + '&Status=' + Status}`);
  }

  GetAllOrganization(url: string) {
    return this._httpService.getRequest<any>(url);
  }

  DeleteOrganization(OrganizationId, organisationType) {
    return this._httpService.getRequest<any>(`${'Organisation/DeleteOrganisationById' + '?id=' + OrganizationId + '&organisationType=' + organisationType}`);
  }

  OrganisationActivationOrDeletion(organizationId: string, status: boolean) {
    return this._httpService.getRequest<any>(`${'Organisation/UpdateOrganisationStatus' + "?id=" + organizationId + "&IsActive=" + status}`);
  }

  UpdateOrganisationAzureAPI(organizationId: string, status: boolean) {
    return this._httpService.getRequest<any>(`${'Organisation/UpdateOrganisationAzureAPI' + "?id=" + organizationId + "&UseAzureAPI=" + status}`);
  }

  EditOrganisation(url: string, organisationModel) {
    return this._httpService.postRequest<any>(url, JSON.stringify(organisationModel));
  }

  GetUnreconcileCommentsReportDetails(unreconciledItemList, CommentsId) {
    var reporturl = "Reporting/" + CommentsId + "/GetUnreconcileCommentsReportDetails";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(unreconciledItemList));
  }

  GetUnreconcileCommentsSaveReportDetails(unreconciledItemList, CommentsId) {
    var reporturl = "Reporting/" + CommentsId + "/GetUnreconcileCommentsSaveReportDetails";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(unreconciledItemList));
  }

  GetoutstandingPayablesCommentsReportDetails(unreconciledItemList, CommentsId) {
    var reporturl = "Reporting/" + CommentsId + "/GetOutstandingPayablesCommentsReportDetails";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(unreconciledItemList));
  }

  DeleteReportDetails(ReportId) {
    return this._httpService.getRequest<any>(`${'Reporting/DeleteReportDetails' + '?ReportId=' + ReportId}`);
  }

  GetBankAccountDetails(url: string, organisationId) {
    return this._httpService.getRequest<any>(`${url + '?organisationId=' + organisationId}`);
  }

  private bankAccountsCache = new Map<string, Observable<any>>();

  /**
   * Org bank accounts, memoised per organisation. The list comes from the
   * accounting integration and only changes on a refresh, but the request is
   * slow enough to leave the payment panel's account field blank on every
   * open. Callers that need it fresh should use GetBankAccountDetails.
   */
  GetBankAccountDetailsCached(organisationId: string): Observable<any> {
    if (!this.bankAccountsCache.has(organisationId)) {
      this.bankAccountsCache.set(
        organisationId,
        this.GetBankAccountDetails('Organisation/GetBankAccountDetails', organisationId)
          .pipe(shareReplay({ bufferSize: 1, refCount: false })));
    }

    return this.bankAccountsCache.get(organisationId);
  }

  clearBankAccountsCache(organisationId?: string): void {
    if (organisationId) {
      this.bankAccountsCache.delete(organisationId);
    } else {
      this.bankAccountsCache.clear();
    }
  }

  GetCardDetails(url: string, accountId) {
    return this._httpService.getRequest<any>(`${url + '?accountId=' + accountId}`);
  }

  EditCompany(url: string, organisationModel) {
    return this._httpService.postRequest<any>(url, JSON.stringify(organisationModel));
  }

  OutstandingBatchPayment(OrganizationId, BatchPayments) {
    var reporturl = "Reporting/" + OrganizationId + "/OutstandingBatchPayment";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(BatchPayments));
  }

  OutstandingBatchPaymentFromInbox(OrganizationId, BatchPayments) {
    var reporturl = "Reporting/" + OrganizationId + "/OutstandingBatchPaymentFromInbox";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(BatchPayments));
  }

  saveBatchPayment(batchPayment: BatchPaymentHeader) {
    return this._httpService.postRequest<string>('Reporting/SaveBatchPayment', batchPayment);
  }

  updateBatchPayment(batchPayment: BatchPaymentHeader) {
    return this._httpService.postRequest<string>('Reporting/UpdateBatchPayment', batchPayment);
  }

  deleteBatchPayment(paymentId: string) {
    return this._httpService.deleteRequest(`Reporting/DeleteBatchPayment?paymentId=${paymentId}`);
  }

  GetOrganisationsDetailsByUsersLoginId(organizationId: string) {
    return this._httpService.getRequest<any>(`${'Organisation/GetOrganisationsDetailsByUsersLoginId' + "?OrganisationId=" + organizationId + "&userId=" + sessionStorage.Id}`);
  }

  TestHit() {
    return this._httpService.getRequest<any>(`${'Organisation/TestHit'}`);
  }

  GetOrganisationsDocumentLimit(organizationId: string) {
    return this._httpService.getRequest<any>(`${'Organisation/GetOrganisationsDocumentLimit' + "?OrganisationId=" + organizationId}`);
  }

  SaveOutstandingPayableReportName(params) {
    return this._httpService.postRequest<any>(`${'Reporting/SaveOutstandingPayableReportName'}`, JSON.stringify(params));
  }

  GetReportName(reportId: string, organizationId: string) {
    return this._httpService.getRequest<any>(`${'Reporting/GetReportName' + "?OrganisationId=" + organizationId + "&ReportId=" + reportId}`);
  }

  SetDefaultAccount(accountId, organisationId) {
    return this._httpService.getRequest<any>(`${'Organisation/SetDefaultAccount' + "?accountId=" + accountId + "&organisationId=" + organisationId}`);
  }

  SetRemoveDefaultAccount(accountId, organisationId, isDefaultAccount) {
    return this._httpService.getRequest<any>(`${'Organisation/SetRemoveDefaultAccount' + "?accountId=" + accountId + "&organisationId=" + organisationId + "&isDefault=" + isDefaultAccount}`);
  }

  UpdateAssignToAccount(accountId, organisationId, userId) {
    return this._httpService.getRequest<any>(`${'Organisation/UpdateAssignToAccount' + "?accountId=" + accountId + "&organisationId=" + organisationId + "&userId=" + userId}`);
  }

  SetLimitsForDoc(usageLimit, userLimit, organisationId, modelId) {
    return this._httpService.getRequest<any>(`${'Organisation/SetLimitsForDoc' + "?usageLimit=" + usageLimit + "&userLimit=" + userLimit + "&organisationId=" + organisationId + "&modelId=" + modelId}`);
  }

  GetOutstandingPayablesCommentsSaveReportDetails(unreconciledItemList, CommentsId) {
    var reporturl = "Reporting/" + CommentsId + "/GetOutstandingPayablesCommentsSaveReportDetails";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(unreconciledItemList));
  }

  SaveExcludeItem(ReportId, excludeData) {
    var reporturl = "Reporting/" + ReportId + "/SetExludeOutstandingPayableRecord";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(excludeData));
  }

  UpdateStatusOfTask(TaskId, Complete) {
    return this._httpService.getRequest<any>(`${'Dashboard/UpdateStatusOfTask?TaskId=' + TaskId + "&Complete=" + Complete}`);
  }

  UpdateTask(taskId, description, assignedToUserId, name) {
    return this._httpService.getRequest<any>(`${'Dashboard/UpdateTask?TaskId=' + taskId + "&description=" + description + "&assignToUserId=" + assignedToUserId + "&name=" + name}`);
  }

  GetAllBillsToPayItems(reportDataModel) {
    var reporturl = "BillsToPay/GetAllBillsToPayItems";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(reportDataModel));
  }

  GetCommentsDetails(reportDataModel) {
    var reporturl = "Reporting/GetCommentsDetails";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(reportDataModel));
  }

  GetBatchPaymentList(organisationId) {
    return this._httpService.getRequest<any>(`${'Reporting/GetBatchPaymentList' + "?organisationId=" + organisationId}`);
  }

  SetPayableReportDataSelected(supplier, contactId, isChecked, reportId) {
    return this._httpService.getRequest<any>(`${'Reporting/SetPayableReportDataSelected?supplier=' + supplier + "&contactId=" + contactId + "&isChecked=" + isChecked + "&reportId=" + reportId}`);
  }

  SetPayableReportDataSelectedByInvoiceNumber(reportDataModel) {
    return this._httpService.postRequest<any>(`${'Reporting/SetPayableReportDataSelectedByInvoiceNumber'}`, JSON.stringify(reportDataModel));
  }

  SetPayableReportSelectedByReportId(isChecked, reportId) {
    return this._httpService.getRequest<any>(`${'Reporting/SetPayableReportSelectedByReportId?isChecked=' + isChecked + "&reportId=" + reportId}`);
  }

  GetBankAccountDetailsBySupplier(contactId, organizationId: string) {
    return this._httpService.getRequest<any>(`${'Reporting/GetBankAccountDetailsBySupplier?contactId=' + contactId}&organizationId=${organizationId}`);
  }

  UpdateBankAccount(bankAccountModel) {
    return this._httpService.postRequest<any>('Reporting/UpdateBankAccount', JSON.stringify(bankAccountModel));
  }

  CreateBankAccountDetails(bankAccountModel) {
    return this._httpService.postRequest<any>('Reporting/CreateBankAccount', JSON.stringify(bankAccountModel));
  }

  CreateSyncedAccountDetails(bankAccountModel) {
    return this._httpService.postRequest<any>('Reporting/CreateSyncedAccount', JSON.stringify(bankAccountModel));
  }

  CreateCardDetails(bankAccountModel) {
    return this._httpService.postRequest<any>('Reporting/CreateCard', JSON.stringify(bankAccountModel));
  }

  UpdateOutstandingPaymentAmount(OutstandingPayablesRequestModel) {
    var reporturl = "Reporting/UpdateOutstandingPaymentAmount";
    return this._httpService.postRequest<any>(reporturl, JSON.stringify(OutstandingPayablesRequestModel));
  }

  InsertApprovals(commentdetailsModel) {
    var modaldata = JSON.stringify(commentdetailsModel)
    return this._httpService.postRequest<any>('OutstandingPayable/InsertApprovals', modaldata);
  }

  CreateNewUser(newUserRequest) {
    return this._httpService.postRequest<any>('Reporting/CreateNewUser', JSON.stringify(newUserRequest));
  }

  GetSavedReportByReportId(reportDataModel) {
    var reporturl = this.baseUrl + "Reporting/GetSavedReportByReportId";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(reportDataModel));
  }

  GetUnreconciledReportData(reportDataModel) {
    var reporturl = this.baseUrl + "Reporting/GetUnreconciledReportData";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(reportDataModel));
  }


  GetUnreconciledReportList(reportDataModel) {
    var reporturl = this.baseUrl + "Reporting/GetUnreconciledReportList";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(reportDataModel));
  }

  sendAssignedNotification(reportDataModel) {
    var reporturl = this.baseUrl + "Reporting/sendAssignedNotification";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(reportDataModel));
  }

  saveTransactionStatus(reportDataModel) {
    var reporturl = this.baseUrl + "Reporting/SaveBankTransactionAssignStatus";
    return this._httpService.postRequest<any>(`${reporturl}`, reportDataModel);
  }


  InsertUpdateUserLastLoggedOrganization(UserId, OrganizationId) {
    return this._httpService.getRequest<any>(`${'Organisation/InsertUpdateUserLastLoggedOrganization?UserId=' + UserId + "&OrganizationId=" + OrganizationId}`);
  }

  GetUserLastLoggedOrganization(UserId) {
    return this._httpService.getRequest<any>(`${'Organisation/GetUserLastLoggedOrganization?UserId=' + UserId}`);
  }

  GetOrgUserLoggedOrganization(UserId, organizationId) {
    return this._httpService.getRequest<any>(`${'Organisation/GetUserLoggedOrganization?UserId=' + UserId + '&organizationId=' + organizationId}`);
  }

  getTaxRates(organizationId: string, isSettings: boolean = false) {
    return this._httpService.getRequest<TaxRate[]>(`TaxRates/getTaxRates?id=${organizationId}&isSettings=` + isSettings);
  }

  getTrackingCategories(organizationId: string) {
    return this._httpService.getRequest<TrackingCategory[]>(`TrackingCategories/getTrackingCategories?id=${organizationId}`);
  }

  UpdateScheduleDateBills(reportDataModel) {
    var reporturl = "BillsToPay/Update_ScheduleDateBills";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(reportDataModel));
  }

  RemoveXeroConnection(OrganizationId, organisationType, userId: string = '', gmailAccountId: number = 0) {
    return this._httpService.getRequest<any>(`${'Organisation/RemoveXeroConnection' + '?id=' + OrganizationId + '&organisationType=' + organisationType + (userId != '' ? '&userId=' + userId : '') + (gmailAccountId != 0 ? '&gmailAccountId=' + gmailAccountId : '')}`);
  }

  RefreshXeroConnection(OrganizationId) {
    return this._httpService.getRequest<any>(`${'Organisation/RefreshXeroConnection' + '?id=' + OrganizationId}`);
  }

  getOrganizationCurrencies(organizationId: string) {
    return this._httpService.getRequest<string[]>(`${'Organisation/GetOrganizationCurrencies' + '?organisationId=' + organizationId}`);
  }

  getOrganizationCustomFields(organizationId: string, invoiceId: string, isInvoice: boolean, isContact: boolean, isBankTransaction: boolean) {
    return this._httpService.getRequest<customFields[]>(`${'invoices/GetOrganizationCustomFields' + '?organisationId=' + organizationId + '&invoiceId=' + invoiceId + '&isInvoice=' + isInvoice + '&isContact=' + isContact + '&isBankTransaction=' + isBankTransaction}`);
  }

  getOrganizationShortData(organizationId: string) {
    return this._httpService.getRequest<OrganizationShortData>(`${'Organisation/GetOrganizationShortData' + '?organisationId=' + organizationId}`);
  }

  getOrganizationLastRefreshLog(organizationId: string) {
    return this._httpService.getRequest<RefreshLog[]>(`${'Organisation/GetOrganizationLastRefreshLog' + '?organisationId=' + organizationId}`);
  }

  updateOrganizationLastRefreshLog(organizationId: string, email: string) {
    var modaldata =
    {
      organizationId: organizationId,
      email: email
    }
    return this._httpService.getRequest<any>(`${'Organisation/UpdateOrganizationLastRefreshLog?organisationId=' + organizationId + '&email=' + email}`, email);
  }

  // Refresh a single accounting (Xero/QuickBooks) data table for an org.
  // Used by the admin org Integrations view to re-sync one API at a time.
  // `refreshTable` is the RefreshLog [Description] value (e.g. 'Contacts',
  // 'Unpaid Invoices'). Synchronous on the backend — returns the updated
  // RefreshLog row { refreshTable, refreshStatus, endAt } (SD-1162).
  refreshOrganizationDataTable(organizationId: string, refreshTable: string) {
    return this._httpService.getRequest<any>(`${'Organisation/RefreshOrganizationDataTable?organisationId=' + organizationId + '&refreshTable=' + encodeURIComponent(refreshTable)}`);
  }

  SyncNoAccountingData(model: any) {

    return this._httpService.postRequest<any>(`${'Organisation/SyncNoAccountingData'}`, JSON.stringify(model));
  }

  getAccountsList(organizationId: string) {
    return this._httpService.getRequest('Organisation/OrganisationAccountsList' + "?organisationId=" + organizationId);
  }

  getTaxRatesList(organizationId: string) {
    return this._httpService.getRequest('Organisation/OrganisationTaxRatesList' + "?organisationId=" + organizationId);
  }

  getCurrenciesList(organizationId: string) {
    return this._httpService.getRequest('Organisation/OrganisationCurrenciesList' + "?organisationId=" + organizationId);
  }

  getTrackingList(organizationId: string) {
    return this._httpService.getRequest('Organisation/OrganisationTrackingList' + "?organisationId=" + organizationId);
  }

  getBankingList(organizationId: string) {
    return this._httpService.getRequest('Organisation/OrganisationBankingList' + "?organisationId=" + organizationId);
  }

  getOnboardingQuestions() {
    return this._httpService.getRequest('Account/GetAllOnboardingQuestions');
  }

  UpdateOnboardingAnswers(question) {
    var reporturl = "Account/InsertOnboardingAnswer";
    return this._httpService.postRequest<any>(`${reporturl}`, JSON.stringify(question));
  }

  getAvailableAccountGroups(organizationId: string) {
    return this._httpService.getRequest<any>(`Organisation/getAvailableAccountGroups?organisationId=${organizationId}`);
  }

  CreateUserAccountGroup(name) {
    // Encoded, so a name with & or # doesn't cut the query string short.
    return this._httpService.getRequest<any>('Organisation/CreateUserAccountGroup?name=' + encodeURIComponent(name) + '&organizationId=' + this.selectedOrganization);
  }

  CreateUserAccountGroups(accountList) {
    return this._httpService.postRequest<any>('Organisation/CreateUserAccountGroups', JSON.stringify(accountList));
  }

  deleteUserGroup(accountId: string, groupId: string) {
    return this._httpService.deleteRequest(`Organisation/deleteUserGroup?accountId=${accountId}&groupId=${groupId}`);
  }
  /**
   * SD-1200: is the AI agent available to this organisation?
   *
   * The same gate the paperwork assistant reads (Reporting/CheckAiAgentAccess), lifted into the
   * service so a second AI feature doesn't hand-roll the URL. Answers a bare true/false.
   */
  checkAiAgentAccess(organizationId: string) {
    return this._httpService.getRequest<boolean>(
      'Reporting/CheckAiAgentAccess?organizationId=' + encodeURIComponent(organizationId || ''));
  }

  getIntegrationCredentials(type: string) {
    return this._httpService.getRequest<any>(`Organisation/getIntegrationCredentials?type=${type}`);
  }
  getPaymentBatchesByOrganizationId(organisationId: string) {
    return this._httpService.getRequest<any>('BillsToPay/getPaymentBatchesByOrganizationId', { organisationId: organisationId });
  }
  UpdateGmailSubscription(email: string) {
    return this._httpService.getRequest<any>('Gmail/SetupWatch?email=' + email);
  }
  addSyncedAccount(accountModel) {
    return this._httpService.postRequest<any>('Organisation/AddSyncedAccount', JSON.stringify(accountModel));
  }
  ChangeGmailCategorisationStatus(email: string, isCategorise: boolean) {
    return this._httpService.getRequest<any>(`Organisation/ChangeGmailCategorisationStatus?organizationId=${this.selectedOrganization}&email=${email}&isCategorise=${isCategorise}`);
  }
  updateAccountName(payload) {
    return this._httpService.postRequest<any>('Organisation/UpdateAccountName', JSON.stringify(payload));
  }
  AddBonusCredit({ orgId, amount, expiryDate, isReoccurring }: { orgId: string, amount: number, expiryDate: string, isReoccurring: boolean }) {
    return this._httpService.getRequest<any>(`${'Organisation/AddBonusCredits?OrganizationId=' + orgId + "&credits=" + amount + "&expiryDate=" + expiryDate + "&isReoccurring=" + isReoccurring}`);
  }
  GetAllCredits(organizationId: string) {
    return this._httpService.getRequest<any>('Organisation/GetBonusCredits?organizationId=' + organizationId);
  }

  // Org-level invoice details panel display settings (SD-1177).
  getInvoiceDisplaySettings(organisationId: string) {
    return this._httpService.getRequest<{ settings: InvoiceDisplaySettings }>(`display-settings/invoice-panel?organisationId=${organisationId}`);
  }

  updateInvoiceDisplaySettings(payload: { organisationId: string, settings: InvoiceDisplaySettings }) {
    return this._httpService.putRequest('display-settings/invoice-panel', payload);
  }

  setInvoiceDisplaySettings(settings: InvoiceDisplaySettings) {
    this.invoiceDisplaySettings.next(settings);
  }
}
